import React from 'react'
import styled, { keyframes } from 'styled-components'
import { sendLoginCode, login } from './h5Session'

const COUNTDOWN_SECONDS = 60
const MOBILE_PATTERN = /^1[3-9]\d{9}$/
const MIN_CODE_LENGTH = 4

const THEME = '#26C5AB'
const DANGER = '#FF4D4F'
const FIELD_BG = '#F5F6F8'

// 后端错误码 → 提示文案与出错的输入框
const ERRORS = {
  INVALID_CODE: { field: 'code', text: '验证码不正确，请重新输入' },
  TOO_MANY_FAILURES: { field: 'code' },
  PATIENT_NOT_FOUND: { field: 'mobile' },
  TOO_MANY_REQUESTS: { field: 'code' },
  SMS_FAILED: { field: 'code' },
}

const toError = (e, fallback) => {
  const mapped = ERRORS[e && e.code] || {}
  return { field: mapped.field || 'code', text: mapped.text || (e && e.message) || fallback }
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to { opacity: 1; transform: scale(1); }
`

const Mask = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 0.2s ease-out;
`

// 页面本身没有指定字体，不设的话部分 WebView 会回落到宋体
const Card = styled.div`
  box-sizing: border-box;
  width: calc(100vw - 60px);
  max-width: 320px;
  padding: 28px 22px 22px;
  border-radius: 12px;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', 'Microsoft YaHei', sans-serif;
  animation: ${popIn} 0.2s ease-out;
  & input,
  & button {
    font-family: inherit;
  }
`

const Title = styled.div`
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: #222;
  text-align: center;
`

const Tip = styled.div`
  margin: 10px 0 22px;
  font-size: 14px;
  line-height: 20px;
  color: #999;
  text-align: center;
`

// 常态灰底；聚焦白底加主题色描边；出错描红边。边框始终占位，切换状态不跳动
const Field = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 16px;
  border: 1px solid ${(p) => (p.error ? DANGER : p.focused ? THEME : 'transparent')};
  border-radius: 8px;
  background: ${(p) => (p.focused || p.error ? '#fff' : FIELD_BG)};
`

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: #222;
  -webkit-appearance: none;
  &::placeholder {
    color: #aaa;
  }
`

const ClearButton = styled.button`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: -6px;
  padding: 0;
  border: none;
  background: transparent;
`

const Divider = styled.span`
  flex: none;
  width: 1px;
  height: 18px;
  margin: 0 12px;
  background: #e0e0e0;
`

const CodeButton = styled.button`
  flex: none;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 14px;
  color: ${(p) => (p.active ? THEME : p.counting ? '#666' : '#aaa')};
`

const ErrorText = styled.div`
  margin-top: 8px;
  font-size: 13px;
  line-height: 18px;
  color: ${DANGER};
  text-align: left;
`

const Actions = styled.div`
  display: flex;
  margin-top: 24px;
`

const Pill = styled.button`
  flex: 1;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 22px;
  font-size: 16px;
  font-weight: 500;
  & + & {
    margin-left: 12px;
  }
`

const CancelButton = styled(Pill)`
  background: ${FIELD_BG};
  color: #222;
`

// 不可点时用半透明的主题色，与 UI 稿一致
const SubmitButton = styled(Pill)`
  background: ${THEME};
  color: #fff;
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
`

const ClearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <circle cx="8" cy="8" r="8" fill="#C8C9CC" />
    <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
)

/**
 * 评论、点赞等需要身份的操作弹出此框。
 * 阅读文章不受影响——只有用户主动写入时才会出现。
 *
 * 点遮罩不关闭：输入时容易误触，而取消按钮就在下方。
 */
export class LoginModal extends React.Component {
  state = {
    mobile: '',
    code: '',
    countdown: 0,
    sending: false,
    submitting: false,
    focused: null,
    error: null,
  }

  componentDidUpdate(prevProps) {
    // 每次重新打开：清掉上次的验证码和错误；手机号保留，倒计时继续走
    if (this.props.visible && !prevProps.visible) {
      this.setState({ code: '', error: null, focused: null })
      this.lockScroll(true)
    } else if (!this.props.visible && prevProps.visible) {
      this.lockScroll(false)
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer)
    this.lockScroll(false)
  }

  lockScroll(locked) {
    if (typeof document === 'undefined') return
    document.body.style.overflow = locked ? 'hidden' : ''
  }

  startCountdown = () => {
    this.setState({ countdown: COUNTDOWN_SECONDS })
    clearInterval(this.timer)
    this.timer = setInterval(() => {
      this.setState(({ countdown }) => {
        if (countdown <= 1) clearInterval(this.timer)
        return { countdown: Math.max(0, countdown - 1) }
      })
    }, 1000)
  }

  // 改动出错的那一栏时清掉提示
  clearErrorOf = (field) => {
    if (this.state.error && this.state.error.field === field) this.setState({ error: null })
  }

  handleMobileChange = (e) => {
    this.setState({ mobile: e.target.value.replace(/\D/g, '').slice(0, 11) })
    this.clearErrorOf('mobile')
  }

  handleCodeChange = (e) => {
    this.setState({ code: e.target.value.replace(/\D/g, '').slice(0, 6) })
    this.clearErrorOf('code')
  }

  handleClearMobile = () => {
    this.setState({ mobile: '' })
    this.clearErrorOf('mobile')
    this.mobileInput && this.mobileInput.focus()
  }

  handleSendCode = async () => {
    const { uri } = this.props
    const { mobile, countdown, sending } = this.state
    if (countdown > 0 || sending) return
    if (!MOBILE_PATTERN.test(mobile)) {
      this.setState({ error: { field: 'mobile', text: '请输入正确的手机号' } })
      return
    }
    this.setState({ sending: true, error: null })
    try {
      await sendLoginCode(uri, mobile)
      this.startCountdown()
      // 发送成功后直接把光标放到验证码框
      this.codeInput && this.codeInput.focus()
    } catch (e) {
      this.setState({ error: toError(e, '验证码发送失败，请稍后再试') })
    } finally {
      this.setState({ sending: false })
    }
  }

  handleSubmit = async () => {
    const { uri, onSuccess } = this.props
    const { mobile, code, submitting } = this.state
    if (submitting || !this.canSubmit()) return
    if (!MOBILE_PATTERN.test(mobile)) {
      this.setState({ error: { field: 'mobile', text: '请输入正确的手机号' } })
      return
    }
    this.setState({ submitting: true, error: null })
    try {
      const result = await login(uri, mobile, code)
      this.setState({ submitting: false, code: '' })
      onSuccess && onSuccess(result)
    } catch (e) {
      this.setState({ submitting: false, error: toError(e, '登录失败，请稍后再试') })
    }
  }

  canSubmit() {
    const { mobile, code } = this.state
    return mobile.length === 11 && code.length >= MIN_CODE_LENGTH
  }

  renderError(field) {
    const { error } = this.state
    return error && error.field === field ? <ErrorText role="alert">{error.text}</ErrorText> : null
  }

  render() {
    const { visible, onClose } = this.props
    if (!visible) return null

    const { mobile, code, countdown, sending, submitting, focused, error } = this.state
    const counting = countdown > 0
    const codeText = counting ? `${countdown}s 后重发` : sending ? '发送中' : '获取验证码'
    const errorField = error && error.field
    const submittable = this.canSubmit() && !submitting

    return (
      <Mask>
        <Card role="dialog" aria-modal="true" aria-labelledby="h5-login-title">
          <Title id="h5-login-title">登录后参与互动</Title>
          {/* 按 UI 稿在「共同照护」后断行，避免把「手机号」拆到两行 */}
          <Tip>
            评论、点赞需要先登录，使用共同照护
            <br />
            App 的手机号即可
          </Tip>

          <Field focused={focused === 'mobile'} error={errorField === 'mobile'}>
            <Input
              innerRef={(el) => (this.mobileInput = el)}
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={11}
              placeholder="请输入手机号"
              value={mobile}
              onChange={this.handleMobileChange}
              onFocus={() => this.setState({ focused: 'mobile' })}
              onBlur={() => this.setState({ focused: null })}
            />
            {mobile ? (
              // 按下时阻止默认行为，避免输入框先失焦导致键盘收起再弹出
              <ClearButton
                type="button"
                aria-label="清除手机号"
                onMouseDown={(e) => e.preventDefault()}
                onClick={this.handleClearMobile}
              >
                <ClearIcon />
              </ClearButton>
            ) : null}
          </Field>
          {this.renderError('mobile')}

          <Field focused={focused === 'code'} error={errorField === 'code'} style={{ marginTop: 16 }}>
            <Input
              innerRef={(el) => (this.codeInput = el)}
              type="tel"
              inputMode="numeric"
              // iOS 可从短信里自动带出验证码
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="请输入验证码"
              value={code}
              onChange={this.handleCodeChange}
              onFocus={() => this.setState({ focused: 'code' })}
              onBlur={() => this.setState({ focused: null })}
            />
            <Divider />
            <CodeButton
              type="button"
              active={!counting && !sending && MOBILE_PATTERN.test(mobile)}
              counting={counting}
              disabled={counting || sending}
              onMouseDown={(e) => e.preventDefault()}
              onClick={this.handleSendCode}
            >
              {codeText}
            </CodeButton>
          </Field>
          {this.renderError('code')}

          <Actions>
            <CancelButton type="button" onClick={onClose}>
              取消
            </CancelButton>
            <SubmitButton type="button" disabled={!submittable} onClick={this.handleSubmit}>
              {submitting ? '登录中' : '登录'}
            </SubmitButton>
          </Actions>
        </Card>
      </Mask>
    )
  }
}

export default LoginModal



// WEBPACK FOOTER //
// ./src/common/LoginModal.js